import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, LoaderIcon, PlugIcon } from '@utils/icons';
import { Button } from '@components/ui/Button/Button';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './PaneState.module.css';

/* ------------------------------------------------------------------------ *
 * Shared, page-scoped pieces for the Project Merge board: the spinner, the
 * Genba/Trusstor status chips, the skeleton list, and the generic
 * empty/error/connect state panel. Kept together so the two panes (and the
 * bridge) stay DRY. (The button is the shared ui/Button — this page no longer
 * hand-rolls one.)
 * ------------------------------------------------------------------------ */

/** LoaderIcon wrapped so the `.spin` CSS animation applies (icons accept only
    `size`). Respects prefers-reduced-motion via the stylesheet. */
export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span className={styles.spin} aria-hidden="true">
      <LoaderIcon size={size} />
    </span>
  );
}

const GENBA_STATUS_CLASS: Record<string, string | undefined> = {
  'under-construction': styles.sActive,
  'before-construction': styles.sPre,
  negotiation: styles.sPre,
  contracted: styles.sPre,
  completion: styles.sDone,
  canceled: styles.sDone,
};
const GENBA_STATUS_LABEL_KEY: Record<string, string> = {
  negotiation: 'projectMerge.status.negotiation',
  contracted: 'projectMerge.status.contracted',
  'before-construction': 'projectMerge.status.beforeConstruction',
  'under-construction': 'projectMerge.status.underConstruction',
  completion: 'projectMerge.status.completion',
  canceled: 'projectMerge.status.canceled',
};

/** Genba lifecycle status chip — coloured by the stable status code (§6-5) and
    labelled from d-orca i18n (so it follows the user's locale), falling back to
    the upstream's localized statusName for unenumerated codes. Colour-independent
    via dot + text. */
export function GenbaStatusChip({ status, statusName }: { status: string; statusName: string }) {
  const { t } = useTranslation();
  const cls = GENBA_STATUS_CLASS[status] ?? styles.sDone;
  const labelKey = GENBA_STATUS_LABEL_KEY[status];
  return (
    <span className={`${styles.chip} ${cls}`} title={statusName}>
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.chipJp}>{labelKey ? t(labelKey) : statusName}</span>
    </span>
  );
}

/** Trusstor project enabled/disabled chip. */
export function EnabledChip({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  return enabled ? (
    <span className={`${styles.chip} ${styles.sEnabled}`}>
      <span className={styles.dot} aria-hidden="true" />
      {t('projectMerge.row.enabled')}
    </span>
  ) : (
    <span className={`${styles.chip} ${styles.sDisabled}`}>
      <span className={styles.dot} aria-hidden="true" />
      {t('projectMerge.row.disabled')}
    </span>
  );
}

/** "Linked" chip used on already-linked rows. */
export function LinkedChip({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className={`${styles.chip} ${styles.linked}`}>
      {icon}
      {label}
    </span>
  );
}

/** Shimmer placeholder rows shown while a pane is loading. */
export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <ScrollArea
      className={styles.paneList}
      viewportClassName={styles.paneListBody}
      viewportProps={{ 'aria-busy': true, 'data-testid': 'pane-skeleton' }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div className={styles.skelRow} key={`skel-${i}`}>
          <div className={styles.skelTop}>
            <span
              className={`${styles.skel} ${styles.skelTitle}`}
              style={{ '--skel-w': `${52 + (i % 3) * 12}%` } as CSSProperties}
            />
            <span className={`${styles.skel} ${styles.skelMeta}`} />
          </div>
          <span
            className={`${styles.skel} ${styles.skelSub}`}
            style={{ '--skel-w': `${38 + (i % 4) * 9}%` } as CSSProperties}
          />
        </div>
      ))}
    </ScrollArea>
  );
}

type StateVariant = 'neutral' | 'error' | 'connect';

interface IStatePanelProps {
  variant: StateVariant;
  icon: ReactNode;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}

interface IConnectPromptProps {
  titleKey: string;
  bodyKey: string;
  /** CTA label key — "Connect …" (never bound) or "Reconnect …" (expired). */
  ctaKey: string;
  onConnect: () => void;
}

/** The (re)connect prompt filling a pane's body — a `connect` StatePanel with
    the shared plug icon + arrowed CTA. Only the copy differs across its four
    call sites (Genba/Trusstor × noCreds/reauth), so it takes i18n keys. */
export function ConnectPrompt({ titleKey, bodyKey, ctaKey, onConnect }: IConnectPromptProps) {
  const { t } = useTranslation();
  return (
    <StatePanel
      variant="connect"
      icon={<PlugIcon size={24} />}
      title={t(titleKey)}
      actions={
        <Button variant="primary" size="sm" onClick={onConnect}>
          <PlugIcon size={14} />
          {t(ctaKey)}
          <ArrowRightIcon size={13} />
        </Button>
      }
    >
      {t(bodyKey)}
    </StatePanel>
  );
}

/** Generic empty / error / connect panel filling a pane's body. */
export function StatePanel({ variant, icon, title, children, actions }: IStatePanelProps) {
  const variantCls =
    variant === 'error' ? styles.siError : variant === 'connect' ? styles.siConnect : styles.siNeutral;
  return (
    <div className={styles.paneState}>
      <div className={styles.paneStateInner}>
        <div className={`${styles.si} ${variantCls}`}>{icon}</div>
        <h4 className={styles.paneStateTitle}>{title}</h4>
        <p className={styles.paneStateBody}>{children}</p>
        {actions ? <div className={styles.paneStateActions}>{actions}</div> : null}
      </div>
    </div>
  );
}
